/**
 * PipeTestingImportModal - Seed Pipe Testing rows from Progress Tracker
 *
 * Modes:
 *  - By Fluid Code: expands selected fluid codes into ISO × test-package rows
 *  - By Test Package: expands selected TP numbers into ISO × TP rows
 *
 * Also supports a manual add and Excel import.
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

const TEST_TYPE_OPTIONS = ['Hydrostatic', 'Pneumatic', 'Service Leak', 'Sensitive Leak', 'Alternative Leak'];

const tabStyle = (active) => ({
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: active ? '600' : '500',
  border: 'none',
  borderBottom: active ? '2px solid #0f766e' : '2px solid transparent',
  background: 'none',
  color: active ? '#0f766e' : '#6b7280',
  cursor: 'pointer',
});

const PipeTestingImportModal = ({ isOpen, onClose, projectId, jobNumber, onImportSuccess, onImportError }) => {
  const { instance, accounts } = useMsal();
  const [mode, setMode] = useState('fluid'); // 'fluid' | 'package'
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [fluidCodes, setFluidCodes] = useState([]);
  const [importedByFc, setImportedByFc] = useState({});
  const [checkedFcs, setCheckedFcs] = useState({});
  const [testPackages, setTestPackages] = useState([]);
  const [importedByTp, setImportedByTp] = useState({});
  const [checkedTps, setCheckedTps] = useState({});
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualRecord, setManualRecord] = useState({
    iso_sheet_number: '', test_package_number: '',
    line_system_id: '', fluid_code: '', system_description: '', test_type: 'Hydrostatic',
  });

  useEffect(() => {
    if (isOpen) {
      setMode('fluid');
      fetchAvailableFluidCodes();
      fetchAvailableTestPackages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchAvailableFluidCodes = async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/pipe-testing/available-fluid-codes/${projectId}`);
      if (response.data.success) {
        const fcs = response.data.data.fluidCodes || [];
        setFluidCodes(fcs);
        setImportedByFc(response.data.data.importedByFluidCode || {});
        const checked = {};
        fcs.forEach(fc => { checked[fc.fluidCode] = false; });
        setCheckedFcs(checked);
      }
    } catch (error) {
      console.error('Error fetching available fluid codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTestPackages = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/pipe-testing/available-test-packages/${projectId}`);
      if (response.data.success) {
        const tps = response.data.data.testPackages || [];
        setTestPackages(tps);
        setImportedByTp(response.data.data.importedByTestPackage || {});
        const checked = {};
        tps.forEach(tp => { checked[tp.testPackage] = false; });
        setCheckedTps(checked);
      }
    } catch (error) {
      console.error('Error fetching available test packages:', error);
    }
  };

  const handleImportFluid = async () => {
    const selected = Object.keys(checkedFcs).filter(fc => checkedFcs[fc]);
    if (selected.length === 0) return;

    try {
      setImporting(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/pipe-testing/import-from-fluid-codes', {
        projectId,
        fluidCodes: selected,
      });
      if (response.data.success) {
        onImportSuccess && onImportSuccess();
      } else {
        onImportError && onImportError(response.data.message);
      }
    } catch (error) {
      onImportError && onImportError(error.message);
    } finally {
      setImporting(false);
    }
  };

  const handleImportPackages = async () => {
    const selected = Object.keys(checkedTps).filter(tp => checkedTps[tp]);
    if (selected.length === 0) return;

    try {
      setImporting(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/pipe-testing/import-from-test-packages', {
        projectId,
        testPackages: selected,
      });
      if (response.data.success) {
        onImportSuccess && onImportSuccess();
      } else {
        onImportError && onImportError(response.data.message);
      }
    } catch (error) {
      onImportError && onImportError(error.message);
    } finally {
      setImporting(false);
    }
  };

  const handleManualAdd = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/pipe-testing/add-record', {
        projectId,
        record: manualRecord,
      });
      if (response.data.success) {
        setManualRecord({
          iso_sheet_number: '', test_package_number: '',
          line_system_id: '', fluid_code: '', system_description: '', test_type: 'Hydrostatic',
        });
        setShowManualAdd(false);
        onImportSuccess && onImportSuccess();
      }
    } catch (error) {
      onImportError && onImportError(error.message);
    }
  };

  const handleToggleAllFluid = (val) => {
    const updated = {};
    Object.keys(checkedFcs).forEach(fc => { updated[fc] = val; });
    setCheckedFcs(updated);
  };

  const handleToggleAllTp = (val) => {
    const updated = {};
    Object.keys(checkedTps).forEach(tp => { updated[tp] = val; });
    setCheckedTps(updated);
  };

  if (!isOpen) return null;

  const checkedFcCount = Object.values(checkedFcs).filter(Boolean).length;
  const checkedTpCount = Object.values(checkedTps).filter(Boolean).length;
  const checkedCount = mode === 'fluid' ? checkedFcCount : checkedTpCount;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', width: '700px', maxHeight: '85vh',
        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #0f766e 0%, #0891b2 100%)', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>Import Systems to Test</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.9 }}>
              Seed Testing Tracker rows as ISO sheet × test package from the Progress Tracker
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer',
          }}>&times;</button>
        </div>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '0 24px', borderBottom: '1px solid #e5e7eb' }}>
          <button type="button" style={tabStyle(mode === 'fluid')} onClick={() => setMode('fluid')}>
            By Fluid Code
          </button>
          <button type="button" style={tabStyle(mode === 'package')} onClick={() => setMode('package')}>
            By Test Package
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading…</div>
          ) : mode === 'fluid' ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>
                  Fluid Codes ({fluidCodes.length} available)
                </h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => handleToggleAllFluid(true)} style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: '#ccfbf1', border: '1px solid #5eead4', borderRadius: '4px', cursor: 'pointer' }}>
                    Select All
                  </button>
                  <button onClick={() => handleToggleAllFluid(false)} style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>
                    Clear All
                  </button>
                </div>
              </div>

              {fluidCodes.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  No fluid codes found in the Progress Tracker for this project.
                </div>
              ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '40px' }}></th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Fluid Code</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>ISOs</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>ISO×TP Rows</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Already Seeded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fluidCodes.map(({ fluidCode, isoCount, candidateCount }) => (
                        <tr key={fluidCode} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: checkedFcs[fluidCode] ? '#f0fdfa' : '#fff' }}>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={checkedFcs[fluidCode] || false}
                              onChange={(e) => setCheckedFcs(prev => ({ ...prev, [fluidCode]: e.target.checked }))}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '8px 12px', fontWeight: '500' }}>{fluidCode}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>{isoCount}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>{candidateCount != null ? candidateCount : isoCount}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center', color: '#6b7280' }}>{importedByFc[fluidCode] || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {checkedFcCount > 0 && (
                <div style={{ padding: '10px 14px', backgroundColor: '#f0fdf4', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: '#166534' }}>
                  {checkedFcCount} fluid code{checkedFcCount > 1 ? 's' : ''} selected — will seed one Pending row per ISO × test package
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>
                  Test Packages ({testPackages.length} available)
                </h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => handleToggleAllTp(true)} style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: '#ccfbf1', border: '1px solid #5eead4', borderRadius: '4px', cursor: 'pointer' }}>
                    Select All
                  </button>
                  <button onClick={() => handleToggleAllTp(false)} style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>
                    Clear All
                  </button>
                </div>
              </div>

              {testPackages.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  No test package numbers found on Progress Tracker ISOs. Fill in Test Package 1 / 2 on the Progress Tracker first.
                </div>
              ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '40px' }}></th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Test Package</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>ISOs</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Already Seeded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testPackages.map(({ testPackage, isoCount }) => (
                        <tr key={testPackage} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: checkedTps[testPackage] ? '#f0fdfa' : '#fff' }}>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={checkedTps[testPackage] || false}
                              onChange={(e) => setCheckedTps(prev => ({ ...prev, [testPackage]: e.target.checked }))}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '8px 12px', fontWeight: '500' }}>{testPackage}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>{isoCount}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center', color: '#6b7280' }}>{importedByTp[testPackage] || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {checkedTpCount > 0 && (
                <div style={{ padding: '10px 14px', backgroundColor: '#f0fdf4', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: '#166534' }}>
                  {checkedTpCount} test package{checkedTpCount > 1 ? 's' : ''} selected — will seed one Pending row per matching ISO
                </div>
              )}
            </>
          )}

          {/* Manual Add Section */}
          <div style={{ marginTop: '8px' }}>
            <button
              onClick={() => setShowManualAdd(!showManualAdd)}
              style={{
                padding: '8px 16px', backgroundColor: '#f8fafc', border: '1px solid #d1d5db',
                borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
              }}
            >
              {showManualAdd ? '- Hide Manual Add' : '+ Manually Add System'}
            </button>

            {showManualAdd && (
              <div style={{ marginTop: '12px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>ISO Sheet Number</label>
                    <input
                      value={manualRecord.iso_sheet_number}
                      onChange={(e) => setManualRecord(prev => ({ ...prev, iso_sheet_number: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', marginTop: '4px', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Test Package Number</label>
                    <input
                      value={manualRecord.test_package_number}
                      onChange={(e) => setManualRecord(prev => ({ ...prev, test_package_number: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', marginTop: '4px', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Line / System ID</label>
                    <input
                      value={manualRecord.line_system_id}
                      onChange={(e) => setManualRecord(prev => ({ ...prev, line_system_id: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', marginTop: '4px', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Fluid Code</label>
                    <input
                      value={manualRecord.fluid_code}
                      onChange={(e) => setManualRecord(prev => ({ ...prev, fluid_code: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', marginTop: '4px', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>System Description</label>
                    <input
                      value={manualRecord.system_description}
                      onChange={(e) => setManualRecord(prev => ({ ...prev, system_description: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', marginTop: '4px', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Type of Testing</label>
                    <select
                      value={manualRecord.test_type}
                      onChange={(e) => setManualRecord(prev => ({ ...prev, test_type: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', marginTop: '4px', fontSize: '13px', boxSizing: 'border-box' }}
                    >
                      {TEST_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>
                <button
                  onClick={handleManualAdd}
                  disabled={!manualRecord.line_system_id && !manualRecord.fluid_code && !manualRecord.iso_sheet_number}
                  style={{
                    marginTop: '12px', padding: '8px 16px',
                    backgroundColor: (!manualRecord.line_system_id && !manualRecord.fluid_code && !manualRecord.iso_sheet_number) ? '#99f6e4' : '#0f766e',
                    color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                  }}
                >
                  Add Record
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Excel Import Section */}
        <div style={{ padding: '0 24px 12px', borderTop: '1px solid #f3f4f6', marginTop: '8px', paddingTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Or import from Excel:</span>
            <label style={{
              padding: '6px 14px', backgroundColor: '#7c3aed', color: 'white', border: 'none',
              borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
            }}>
              Upload Excel
              <input type="file" accept=".xlsx,.xls" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                try {
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('projectId', projectId);
                  const response = await authenticatedApiCall(instance, accounts, 'POST', '/pipe-testing/import-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                  if (response.data.success) {
                    onImportSuccess && onImportSuccess();
                  } else {
                    onImportError && onImportError(response.data.message);
                  }
                } catch (err) {
                  onImportError && onImportError(err.message || 'Excel import failed');
                }
                e.target.value = '';
              }} style={{ display: 'none' }} />
            </label>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'flex-end', gap: '12px',
        }}>
          <button onClick={onClose} style={{
            padding: '8px 20px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
            borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
          }}>
            Cancel
          </button>
          <button
            onClick={mode === 'fluid' ? handleImportFluid : handleImportPackages}
            disabled={importing || checkedCount === 0}
            style={{
              padding: '8px 20px', backgroundColor: importing || checkedCount === 0 ? '#99f6e4' : '#0f766e',
              color: 'white', border: 'none', borderRadius: '6px', cursor: importing ? 'default' : 'pointer',
              fontSize: '14px', fontWeight: '500',
            }}
          >
            {importing
              ? 'Importing...'
              : mode === 'fluid'
                ? `Import ${checkedFcCount > 0 ? checkedFcCount + ' Fluid Code(s)' : '(select fluid codes)'}`
                : `Import ${checkedTpCount > 0 ? checkedTpCount + ' Test Package(s)' : '(select test packages)'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PipeTestingImportModal;
